"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function AboutUsPhilosophy() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="225041" id="haftungsausschluss" className="py-20 md:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={imageRef} className={`relative transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative aspect-[4/3] md:aspect-square lg:aspect-[4/5] rounded-3xl overflow-hidden shadow-lg z-10 border-l-8 border-primary">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650013/img/legal_disclaimer_document.png" alt="Rechtliche Hinweise und Dokumentation" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
            
            {}
            <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-0"></div>
            <div className="absolute -top-8 -right-8 w-48 h-48 bg-secondary/10 rounded-full blur-2xl -z-0"></div>
          </div>

          {}
          <div ref={textRef} className={`flex flex-col gap-6 lg:gap-8 transition-all duration-1000 delay-200 ease-out max-w-2xl ${isTextVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div>
              <span className="text-secondary font-semibold tracking-widest uppercase text-sm mb-3 block">
                Rechtliches
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default leading-tight text-foreground">
                Haftungsausschluss: <br />
                <span className="text-primary">Wichtige Informationen</span>
              </h2>
            </div>

            <div className="space-y-8 text-foreground/80 text-lg leading-relaxed">
              <p className="border-b border-border pb-8"><div>Die Inhalte unserer Website wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte übernehmen wir jedoch keine Gewähr. Als Diensteanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen österreichischen Gesetzen verantwortlich.
</div><div>Einzelne Inhalte, Texte und Bilder auf dieser Website wurden mit Unterstützung von künstlicher Intelligenz (KI) erstellt und vor Veröffentlichung redaktionell geprüft.</div><div>
</div><div>
</div><div>
</div></p>
              <p className="border-b border-border pb-8">Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Für diese fremden Inhalte übernehmen wir daher keine Gewähr. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.</p>
              <p>Die auf dieser Website erstellten Inhalte, Texte, Bilder und Grafiken unterliegen, soweit rechtlich geschützt, dem österreichischen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung oder sonstige Verwendung außerhalb der Grenzen des Urheberrechts bedarf der vorherigen schriftlichen Zustimmung des jeweiligen Rechteinhabers.<br /></p>
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="rounded-[50px] px-8 h-12 text-base bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_0_0_rgba(0,0,0,0.1)] transition-all">
                <Link to="/HomePage">
                  Zur Startseite <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}